import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const FAQContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  padding: '40px 20px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  border: '1px solid #00F0FF',
  borderRadius: '5px',
  marginBottom: '15px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  '&:before': {
    display: 'none',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.9)',
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: '#00F0FF',
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  fontWeight: 'bold',
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  lineHeight: '1.8',
}));

const FAQ: React.FC = () => {
  useEffect(() => {
    // Add FAQ Schema to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Steg Safe?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Steg Safe is an image steganography tool that encrypts data and hides it inside images securely."
          }
        },
        {
          "@type": "Question",
          "name": "Is my image uploaded permanently?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, Steg Safe processes images securely and does not store user files permanently."
          }
        },
        {
          "@type": "Question",
          "name": "What is image steganography?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Image steganography is the process of hiding information inside an image so that the image appears normal."
          }
        },
        {
          "@type": "Question",
          "name": "Is encryption used before hiding data?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, data is encrypted before embedding into the image using AES-256-GCM encryption."
          }
        },
        {
          "@type": "Question",
          "name": "What file formats are supported?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Steg Safe supports PNG, BMP, and JPEG image formats for encoding and decoding operations."
          }
        },
        {
          "@type": "Question",
          "name": "Is Steg Safe free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, Steg Safe is completely free to use with no hidden fees or premium tiers."
          }
        },
        {
          "@type": "Question",
          "name": "How secure is the encryption?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Steg Safe uses AES-256-GCM encryption, which is the same standard used by governments and financial institutions worldwide."
          }
        },
        {
          "@type": "Question",
          "name": "Can I hide any type of data?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can hide text messages and other data. The amount of data that can be hidden depends on the image size and encoding depth."
          }
        }
      ]
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const faqs = [
    {
      question: "What is Steg Safe?",
      answer: "Steg Safe is an image steganography tool that encrypts data and hides it inside images securely."
    },
    {
      question: "Is my image uploaded permanently?",
      answer: "No, Steg Safe processes images securely and does not store user files permanently. All processing happens in real-time and images are never stored on our servers."
    },
    {
      question: "What is image steganography?",
      answer: "Image steganography is the process of hiding information inside an image so that the image appears normal to the human eye, while secretly containing embedded data."
    },
    {
      question: "Is encryption used before hiding data?",
      answer: "Yes, data is encrypted before embedding into the image using AES-256-GCM encryption, providing dual-layer protection for your sensitive information."
    },
    {
      question: "What file formats are supported?",
      answer: "Steg Safe supports PNG, BMP, and JPEG image formats for both encoding and decoding operations."
    },
    {
      question: "Is Steg Safe free to use?",
      answer: "Yes, Steg Safe is completely free to use with no hidden fees, subscriptions, or premium tiers."
    },
    {
      question: "How secure is the encryption?",
      answer: "Steg Safe uses AES-256-GCM encryption with PBKDF2 key derivation, which is the same standard used by governments and financial institutions worldwide."
    },
    {
      question: "Can I hide any type of data?",
      answer: "You can hide text messages and other data. The amount of data that can be hidden depends on the image size and the encoding depth selected (1, 2, or 4 bits)."
    }
  ];

  return (
    <FAQContainer>
      <Container maxWidth="lg">
        <SectionTitle>Frequently Asked Questions</SectionTitle>
        
        {faqs.map((faq, index) => (
          <StyledAccordion key={index}>
            <StyledAccordionSummary expandIcon={<span>▼</span>}>
              <QuestionText>{faq.question}</QuestionText>
            </StyledAccordionSummary>
            <AccordionDetails>
              <AnswerText>{faq.answer}</AnswerText>
            </AccordionDetails>
          </StyledAccordion>
        ))}
      </Container>
    </FAQContainer>
  );
};

export default FAQ;
